import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageService } from 'ngx-webstorage';
import { environment } from '../../environments/environment';
import { NO_ERROR_TOKEN } from '../interceptors/error.interceptor';
import { NO_TEAMS_TOKEN } from '../interceptors/teams.interceptor';
import { ClubKeuze, voegRecentToe } from '../helpers/club-kiezer';

export interface StudioInstaller {
  url: string;
  versie: string;
}

interface RingVersies {
  rings?: Record<string, string>;
  installers?: Record<string, string>;
}

/**
 * Clubkiezer voor een super-admin (24-10-2026, variant D). Praat met auth:
 * welke clubs er zijn en een club openen. Dat laatste geeft een token met de
 * rechten van een clubbeheerder van die club, op naam van de super-admin
 * (zelfde weg als "Open als club" in de beheerconsole). Eigen foutmeldingen
 * in het venster, dus niet de algemene.
 */
@Injectable({ providedIn: 'root' })
export class ClubKiezerService {
  private readonly http = inject(HttpClient);
  private readonly localStorage = inject(LocalStorageService);
  private static readonly RECENT = 'clubkiezer.recent';

  public lijst(): Observable<ClubKeuze[]> {
    return this.http.get<ClubKeuze[]>(`${environment.authUrl}/Account/Impersonate/Clubs`, { context: this.context() });
  }

  /** Opent een club; geeft het nieuwe token. */
  public open(club: string): Observable<string> {
    return this.http.post<{ token: string }>(`${environment.authUrl}/Account/Impersonate`, { club }, { context: this.context() })
      .pipe(map((res) => res.token));
  }

  /**
   * De installer van de studio-app op de prod-ring, de versie van de clubs
   * (eigenaar 28-10-2026: voor een nieuwe studio-pc, net als Fleet → Rings in
   * de beheerconsole). Null als de ring geen geldige link heeft.
   */
  public studioInstaller(): Observable<StudioInstaller | null> {
    return this.http.get<RingVersies>(`${environment.apiUrl}/Device/RingVersions`, { context: this.context() })
      .pipe(map((res) => {
        const url = res?.installers?.prod;
        const versie = res?.rings?.prod;
        return url && versie ? { url, versie } : null;
      }));
  }

  /** Recent geopende clubs van deze browser, nieuwste eerst. */
  public get recent(): string[] {
    try {
      const lijst = this.localStorage.retrieve(ClubKiezerService.RECENT);
      return Array.isArray(lijst) ? lijst : [];
    } catch {
      return [];
    }
  }

  public onthoud(club: string): void {
    try {
      this.localStorage.store(ClubKiezerService.RECENT, voegRecentToe(this.recent, club));
    } catch {
      // Zonder opslag (privévenster) gewoon geen recente lijst.
    }
  }

  private context(): HttpContext {
    return new HttpContext().set(NO_ERROR_TOKEN, true).set(NO_TEAMS_TOKEN, true);
  }
}
